import { useState } from 'react'
import { X, AlertTriangle, CheckCircle, Info, Loader2, ChevronUp, ChevronDown } from 'lucide-react'
import clsx from 'clsx'

// ── Loading spinner ──────────────────────────────────────────────
const SPINNER_SIZES = { sm: 14, md: 20, lg: 28 }
export function Spinner({ size = 'md', className = '' }) {
  const px = typeof size === 'number' ? size : (SPINNER_SIZES[size] ?? 20)
  return <Loader2 size={px} className={clsx('animate-spin text-primary', className)} />
}

// ── Skeleton loader ───────────────────────────────────────────────
export function Skeleton({ className = '' }) {
  return <div className={clsx('skeleton h-4', className)} />
}

// ── Modal ─────────────────────────────────────────────────────────
export function Modal({ open, onClose, title, children, size = 'md' }) {
  // If open prop provided, use it; if omitted, treat as open (caller controls mounting)
  if (open === false) return null
  const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-4xl', xl: 'max-w-5xl' }
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className={clsx('modal-box', widths[size])}
        onClick={e => e.stopPropagation()}
      >
        {title && (
          <div className="flex items-center justify-between p-5 border-b border-surface-border">
            <h2 className="section-title text-base">{title}</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors p-1">
              <X size={18} />
            </button>
          </div>
        )}
        <div className="p-5">{children}</div>
      </div>
    </div>
  )
}

// ── Confirm dialog ────────────────────────────────────────────────
export function ConfirmDialog({ open, onClose, onConfirm, title, message, confirmLabel = 'Confirm', danger = false }) {
  return (
    <Modal open={open} onClose={onClose} size="sm">
      <div className="text-center space-y-4">
        <div className={clsx(
          'w-14 h-14 rounded-full flex items-center justify-center mx-auto',
          danger ? 'bg-red-900/40' : 'bg-amber-900/40'
        )}>
          <AlertTriangle size={24} className={danger ? 'text-red-400' : 'text-amber-400'} />
        </div>
        <div>
          <h3 className="text-white font-heading text-lg">{title}</h3>
          <p className="text-gray-400 text-sm mt-1">{message}</p>
        </div>
        <div className="flex gap-3 justify-center pt-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button
            onClick={() => { onConfirm(); onClose() }}
            className={danger ? 'btn-danger' : 'btn-primary'}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </Modal>
  )
}

// ── Status badge ──────────────────────────────────────────────────
const STATUS_MAP = {
  active: 'badge-success',
  delivered: 'badge-success',
  shipped: 'badge-info',
  processing: 'badge-warning',
  pending: 'badge-warning',
  cancelled: 'badge-danger',
  out_of_stock: 'badge-danger',
  low_stock: 'badge-warning',
  inactive: 'badge-neutral',
  vip: 'badge-gold',
}

export function StatusBadge({ status }) {
  const cls = STATUS_MAP[status] || 'badge-neutral'
  return <span className={cls}>{status?.replace(/_/g, ' ')}</span>
}

// ── Empty state ───────────────────────────────────────────────────
export function EmptyState({ icon: Icon, title, message, action }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      {Icon && (
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mb-4">
          <Icon size={28} className="text-gray-600" />
        </div>
      )}
      <h3 className="font-heading text-white text-lg">{title}</h3>
      {message && <p className="text-gray-500 text-sm mt-1 max-w-xs">{message}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

// ── Pagination ────────────────────────────────────────────────────
export function Pagination({ page, total, perPage = 20, onChange, onPerPageChange, perPageOptions = [10, 20, 50, 100], position = 'bottom' }) {
  const totalPages = Math.max(1, Math.ceil(total / perPage))
  const showNav = totalPages > 1
  // Only hide entirely when there's nothing to page AND no size control to show.
  if (!showNav && !onPerPageChange) return null

  const edge = position === 'top'
    ? 'pb-3 border-b border-surface-border mb-3'
    : 'pt-4 border-t border-surface-border mt-2'

  return (
    <div className={clsx('flex items-center justify-between flex-wrap gap-2', edge)}>
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-500">
          {total > 0
            ? <>Showing {(page - 1) * perPage + 1}–{Math.min(page * perPage, total)} of {total.toLocaleString()}</>
            : 'No rows'}
        </span>
        {onPerPageChange && (
          <label className="flex items-center gap-1.5 text-xs text-gray-500">
            Rows:
            <select
              value={perPage}
              onChange={e => onPerPageChange(Number(e.target.value))}
              className="bg-black/40 border border-surface-border rounded-lg px-2 py-1 text-xs text-gray-300 focus:border-primary outline-none cursor-pointer"
            >
              {perPageOptions.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}
      </div>
      {showNav && (
        <div className="flex gap-1">
          <button
            disabled={page === 1}
            onClick={() => onChange(page - 1)}
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30"
          >
            Prev
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const p = Math.max(1, Math.min(page - 2, totalPages - 4)) + i
            return (
              <button
                key={p}
                onClick={() => onChange(p)}
                className={clsx(
                  'w-8 h-8 rounded-lg text-xs font-medium transition-colors',
                  p === page
                    ? 'bg-primary text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                )}
              >
                {p}
              </button>
            )
          })}
          <button
            disabled={page === totalPages}
            onClick={() => onChange(page + 1)}
            className="btn-ghost text-xs px-3 py-1.5 disabled:opacity-30"
          >
            Next
          </button>
        </div>
      )}
    </div>
  )
}

// ── Sort header ───────────────────────────────────────────────────
export function SortHeader({ label, field, sortField, sortDir, onSort }) {
  const active = sortField === field
  return (
    <button
      onClick={() => onSort(field)}
      className="flex items-center gap-1 text-xs font-display font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-300 transition-colors"
    >
      {label}
      <span className="flex flex-col">
        <ChevronUp size={10} className={active && sortDir === 'asc' ? 'text-gold' : 'text-gray-700'} />
        <ChevronDown size={10} className={active && sortDir === 'desc' ? 'text-gold' : 'text-gray-700'} />
      </span>
    </button>
  )
}

// ── Stat card ─────────────────────────────────────────────────────
export function StatCard({ title, value, change, icon: Icon, iconColor = 'text-primary', prefix = '', suffix = '' }) {
  const positive = change >= 0
  return (
    <div className="stat-card">
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-xs text-gray-500 font-display uppercase tracking-wider">{title}</p>
          <p className="font-heading text-2xl text-white mt-1">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
          {change !== undefined && (
            <p className={clsx('text-xs mt-1 font-medium', positive ? 'text-green-400' : 'text-red-400')}>
              {positive ? '↑' : '↓'} {Math.abs(change)}% vs last month
            </p>
          )}
        </div>
        {Icon && (
          <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center">
            <Icon size={18} className={iconColor} />
          </div>
        )}
      </div>
    </div>
  )
}
